import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Checkbox,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/authUtils';
import { getApiBaseUrl } from '../../utils/apiConfig';
import TenantDialog from './TenantDialog';
import { useSnackbar } from '../CentralSnackbar';

const TenantManagement = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [confirmAction, setConfirmAction] = useState({ open: false, title: '', description: '', onConfirm: null });

  // Helper function to check if tenant is default tenant (ID=1)
  const isDefaultTenant = (tenant) => {
    return tenant && tenant.id === 1;
  };

  // Helper function to check if any selected tenant is default tenant
  const hasDefaultTenantSelected = () => {
    return selectedTenants.some(tenantId => {
      const tenant = tenants.find(t => t.id === tenantId);
      return isDefaultTenant(tenant);
    });
  };



  const loadTenants = useCallback(async () => {
    setLoading(true);
    
    try {
      const response = await axios.get(`${getApiBaseUrl()}/admin/tenants/`, {
        headers: {
          ...getAuthHeaders(),
        }
      });
      
      setTenants(response.data);
    } catch (err) {
      console.error('Error loading tenants:', err);
      showError(t('admin.tenantManagement.loadTenantsError'), 'ERR-TENANT-001');
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const filteredTenants = tenants.filter(tenant =>
    tenant.bezeichnung.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tenant.hl7_name && tenant.hl7_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Select all tenants except default tenant (ID=1) since it cannot be deleted
      setSelectedTenants(filteredTenants.filter(tenant => tenant.id !== 1).map(tenant => tenant.id));
    } else {
      setSelectedTenants([]);
    }
  };

  const handleSelectTenant = (tenantId) => {
    setSelectedTenants(prev => 
      prev.includes(tenantId) 
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const getSelectedTenantObjects = () => {
    return tenants.filter(tenant => selectedTenants.includes(tenant.id));
  };

  const handleOpenDialog = (tenant = null) => {
    setCurrentTenant(tenant);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setCurrentTenant(null);
  };

  const handleSaveTenant = async (tenantData) => {
    try {
      let savedTenant;
      
      if (tenantData.id) {
        // Update existing tenant
        const response = await axios.put(`${getApiBaseUrl()}/admin/tenants/${tenantData.id}`, tenantData, {
          headers: {
            ...getAuthHeaders(),
          }
        });
        savedTenant = response.data;
        showSuccess(t('admin.tenantManagement.saveTenantSuccess', { name: tenantData.bezeichnung }));
      } else {
        // Create new tenant
        const response = await axios.post(`${getApiBaseUrl()}/admin/tenants/`, tenantData, {
          headers: {
            ...getAuthHeaders(),
          }
        });
        savedTenant = response.data;
        showSuccess(t('admin.tenantManagement.createTenantSuccess', { name: tenantData.bezeichnung }));
      }

      // Update user-tenant assignments if selectedUsers is provided
      if (tenantData.selectedUsers !== undefined) {
        await axios.put(`${getApiBaseUrl()}/admin/tenants/${savedTenant.id}/users`, {
          user_ids: tenantData.selectedUsers
        }, {
          headers: {
            ...getAuthHeaders(),
          }
        });
      }

      // Reload tenants after successful save
      await loadTenants();
      setSelectedTenants([]);
      
    } catch (err) {
      console.error('Error saving tenant:', err);
      showError(t('admin.tenantManagement.saveTenantError'), 'ERR-TENANT-002');
    }
  };

  const handleDeleteTenants = async () => {
    const selected = getSelectedTenantObjects();
    
    let description;
    if (selected.length === 1) {
      description = t('admin.tenantManagement.deleteConfirmSingle', { name: selected[0].bezeichnung });
    } else {
      description = t('admin.tenantManagement.deleteConfirmMultiple', { count: selected.length });
    }

    setConfirmAction({
      open: true,
      title: t('admin.tenantManagement.deleteTitle'),
      description: description,
      isDeleteAction: true,
      onConfirm: async () => {
        try {
          for (const tenant of selected) {
            await axios.delete(`${getApiBaseUrl()}/admin/tenants/${tenant.id}`, {
              headers: {
                ...getAuthHeaders(),
              }
            });
          }
          
          await loadTenants();
          setSelectedTenants([]);
          
          // Show success message
          const deletedCount = selected.length;
          if (deletedCount === 1) {
            showSuccess(t('admin.tenantManagement.deleteTenantSuccess', { name: selected[0].bezeichnung }));
          } else {
            showSuccess(t('admin.tenantManagement.deleteTenantsSuccess', { count: deletedCount }));
          }
          
        } catch (err) {
          console.error('Error deleting tenants:', err);
          showError(t('admin.tenantManagement.deleteTenantError'), 'ERR-TENANT-003');
        }
      }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon color="primary" />
          {t('admin.tenantManagement.title')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          className="button"
        >
          {t('admin.tenantManagement.addTenant')}
        </Button>
      </Box>

      {/* Search and Actions */}
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
        <TextField
          placeholder={t('admin.tenantManagement.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ marginRight: 1, color: 'text.secondary' }} />
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button
          variant="outlined"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={loadTenants}
        >
          {t('admin.tenantManagement.refresh')}
        </Button>
      </Box>

      {/* Action Bar */}
      <Paper sx={{ padding: 2, marginBottom: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">
            {selectedTenants.length === 0 
              ? t('admin.tenantManagement.noSelected')
              : t('admin.tenantManagement.selectedCount', { count: selectedTenants.length })
            }
          </Typography>
          
          <Divider orientation="vertical" flexItem />
          
          <Tooltip title={t('admin.tenantManagement.editTenant')}>
            <span>
              <IconButton
                size="small"
                disabled={selectedTenants.length !== 1}
                onClick={() => {
                  const tenant = tenants.find(t => t.id === selectedTenants[0]);
                  handleOpenDialog(tenant);
                }}
                aria-label={t('admin.tenantManagement.editTenant')}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          <Tooltip title={hasDefaultTenantSelected() ? t('admin.tenantManagement.defaultTenantProtected') : t('admin.tenantManagement.deleteTenants')}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={selectedTenants.length === 0 || hasDefaultTenantSelected()}
                onClick={handleDeleteTenants}
                aria-label={t('admin.tenantManagement.deleteTenants')}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {/* Tenants Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedTenants.length > 0 && selectedTenants.length < filteredTenants.filter(t => t.id !== 1).length}
                  checked={selectedTenants.length > 0 && selectedTenants.length === filteredTenants.filter(t => t.id !== 1).length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>{t('admin.tenantManagement.tableHeaders.id')}</TableCell>
              <TableCell>{t('admin.tenantManagement.tableHeaders.name')}</TableCell>
              <TableCell>{t('admin.tenantManagement.tableHeaders.hl7Name')}</TableCell>
              <TableCell>{t('admin.tenantManagement.tableHeaders.authorizedUsers')}</TableCell>
              <TableCell>{t('admin.tenantManagement.tableHeaders.status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTenants.map((tenant) => (
              <TableRow key={tenant.id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedTenants.includes(tenant.id)}
                    onChange={() => handleSelectTenant(tenant.id)}
                  />
                </TableCell>
                <TableCell>{tenant.id}</TableCell>
                <TableCell>{tenant.bezeichnung}</TableCell>
                <TableCell>{tenant.hl7_name || '-'}</TableCell>
                <TableCell>{tenant.user_count || 0}</TableCell>
                <TableCell>
                  <Chip
                    label={tenant.is_active ? t('common.active') : t('common.inactive')}
                    size="small"
                    color={tenant.is_active ? 'success' : 'default'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmAction.open}
        onClose={() => setConfirmAction({ ...confirmAction, open: false })}
      >
        <DialogTitle>{confirmAction.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction.description}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmAction({ ...confirmAction, open: false })}
            className="button-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={() => {
              confirmAction.onConfirm();
              setConfirmAction({ ...confirmAction, open: false });
            }} 
            className={confirmAction.isDeleteAction ? "button-delete" : "button"}
          >
            {confirmAction.isDeleteAction ? t('common.delete') : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tenant Dialog */}
      <TenantDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveTenant}
        tenant={currentTenant}
      />
    </Box>
  );
};

export default TenantManagement; 